import React from "react";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSiteInfo } from "../../hooks/useSiteInfo";
import { useContentBlocks } from "../../hooks/useContentBlocks";
import { pickBlock } from "../../helpers/contentHelpers";

const AboutSection = ({ sectionData }) => {
  const { t, language } = useLanguage();
  const { info } = useSiteInfo();
  const pick = (en, ja, key) => (language === "ja" && ja) ? ja : (en ?? t(key));

  // Fetch stat content blocks for this section
  const sectionId = sectionData?.id;
  const { blocks: statBlocks } = useContentBlocks(sectionId, "about", "stat");

  // Build stats — from content blocks or fallback to siteInfo + locale
  const stats = statBlocks.length > 0
    ? statBlocks.map((block) => {
        const picked = pickBlock(block, language);
        return {
          value: picked.value,
          label: picked.label,
          description: picked.description,
        };
      })
    : [
        {
          value: info("stat_talent_placed", t("about.stats.talentPlaced.value")),
          label: t("about.stats.talentPlaced.label"),
          description: t("about.stats.talentPlaced.description"),
        },
        {
          value: info("stat_capital_raised", t("about.stats.capitalRaised.value")),
          label: t("about.stats.capitalRaised.label"),
          description: t("about.stats.capitalRaised.description"),
        },
        {
          value: info("stat_partners", t("about.stats.activePartners.value")),
          label: t("about.stats.activePartners.label"),
          description: t("about.stats.activePartners.description"),
        },
        {
          value: info("stat_success_rate", t("about.stats.successRate.value")),
          label: t("about.stats.successRate.label"),
          description: t("about.stats.successRate.description"),
        },
      ];

  return (
    <section className="py-16 bg-white" id="about-us">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-4xl font-serif-brand font-black text-slate-900 mb-4">
              {pick(sectionData?.titleEn, sectionData?.titleJa, "about.title")}
            </h2>
            <p className="text-base text-slate-500 font-medium">
              {pick(sectionData?.descriptionEn, sectionData?.descriptionJa, "about.description")}
            </p>
          </div>
          <button className="group flex items-center space-x-2 text-subidha-blue font-black hover:text-accent-green transition-colors uppercase text-sm tracking-widest">
            <span>{t("about.exploreImpact")}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-subidha-blue p-8 rounded-[1.5rem] text-white space-y-4 hover:shadow-2xl transition-all transform hover:-translate-y-2"
            >
              <h3 className="text-4xl font-black tracking-tighter">
                {stat.value}
              </h3>
              <div className="space-y-1.5">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-accent-green">
                  {stat.label}
                </p>
                <p className="text-xs leading-relaxed text-blue-100/70 font-medium">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* --- SCROLLING LOGO SECTION --- */}
        <div className="pt-10 border-t border-slate-100 overflow-hidden">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-1 h-5 bg-accent-green rounded-full"></div>
            <p className="text-[#4A5D69] text-xs font-bold uppercase tracking-[0.2em]">
              {t("about.workedWith")}
            </p>
          </div>

          <div className="relative w-full [mask-image:linear-gradient(to_right,transparent,white_10%,white_90%,transparent)]">
            <div className="flex w-max animate-marquee gap-12 lg:gap-20 py-4">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="flex shrink-0 items-center gap-4 opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex gap-1">
                    <div className="flex flex-col gap-1">
                      <div className="w-3 h-3 bg-[#3F8DB1] rounded-full"></div>
                      <div className="w-3 h-3 bg-[#9AC101] rounded-full"></div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="w-3 h-3 bg-[#9AC101] rounded-full"></div>
                      <div className="w-3 h-3 bg-[#3F8DB1] rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-[#132D80] font-bold text-2xl tracking-tight">
                      Invest
                    </span>
                    <span className="text-[#9FC410] font-bold text-2xl tracking-tight">
                      Seed
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
